/* eslint-disable */
import { useState } from 'react';
import { X, Check, Building2, Columns3 } from 'lucide-react';
import {
	parsePastedTable,
	matchPastedRowToProduct,
	getUnitConvertedUnit,
} from '@/Helpers';
import { matchIngredientToProduct } from '@/Components/Modals/MultiRecipeEditModal/MultiRecipeEditModal';
import { notificationServices } from '@/Services';
import { CLOSED_CONFIRMATION } from './createOrdersUiHelpers';
import { META_IDS } from './useStepTable';

const norm = (x) => String(x || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const splitLine = (line) => (line.includes('\t') ? line.split('\t') : line.split(','))
	.map((cell) => String(cell).trim());

// Same header keys parsePastedTable uses — everything else in the pasted
// header row is treated as a store / buyer-location column.
const META_HEADER_KEYS = [
	'productname', 'itemname', 'name',
	'skucode', 'sku', 'clientvariantid', 'clientvariant',
	'uom', 'unit',
	'productionplan', 'qty', 'quantity',
	'sno', 'serialno', 'srno', 'total', 'actions', 'remove',
];

// Company-suffix words that shouldn't break a match ("Sunshine Teahouse
// Private Limited" ≡ "Sunshine Teahouse Pvt Ltd").
const SUFFIX_WORDS = [
	'private', 'pvt', 'limited', 'ltd', 'llp', 'inc',
	'co', 'company', 'corp', 'corporation',
];
const coreName = (x) => String(x || '')
	.toLowerCase()
	.replace(/[^a-z0-9\s]/g, ' ')
	.split(/\s+/)
	.filter((word) => word && !SUFFIX_WORDS.includes(word))
	.join('');

const looseMatch = (a, b) => {
	const na = norm(a);
	const nb = norm(b);
	if (!na || !nb) return false;
	if (na === nb) return true;
	// Suffix-insensitive equality.
	const ca = coreName(a);
	const cb = coreName(b);
	if (ca && cb && ca === cb) return true;
	return na.includes(nb) || nb.includes(na);
};

// Pasted store/location headers -> table columns:
//   1) already a table column                 -> map qty into it
//   2) LOCATION name in the system            -> ADD the column (confirmed)
//   2b) BUYER name (old logic only checked
//       locations) -> its FIRST location      -> confirmed via modal
//   3) store name in the system (order mode)  -> ADD the column (confirmed)
//   4) not in the system at all               -> ADD a RED (unknown) column
const resolvePastedColumns = ({ candidates, headers, lists, modeName }) => {
	const columnMap = []; // { idx, headerId, isNew }
	const newHeaders = [];
	const buyerIdsToAdd = [];
	const unknownNames = [];
	// Assumptions the user confirms in a modal before anything is applied:
	const buyerNameCols = []; // pasted header was a BUYER name -> first location
	const addedCols = []; // system columns not in the table yet -> will be added

	candidates.forEach(({ raw, idx }) => {
		const existing = headers.find(
			(header) => !META_IDS.includes(header.id) &&
				!header._unknownColumn &&
				[
					header.name,
					header.storeName,
					header.shortName,
					header.groupName,
				].some((label) => label && looseMatch(label, raw))
		);
		if (existing) {
			columnMap.push({ idx, headerId: existing.id, isNew: false });
			return;
		}

		if (modeName === 'buyerOrder') {
			// 1) match a location name (what the old paste logic checked)...
			for (const buyer of lists?.buyerList || []) {
				const location = (buyer?.children || []).find(
					(loc) => looseMatch(loc?.label || loc?.name, raw)
				);
				if (location) {
					newHeaders.push({
						name: location.label || location.name,
						id: location.value,
						groupName: buyer?.label || buyer?.name,
					});
					buyerIdsToAdd.push(location.value);
					columnMap.push({ idx, headerId: location.value, isNew: true });
					addedCols.push({
						raw,
						resolvedName: `${buyer?.label || buyer?.name} — ${location.label || location.name}`,
					});
					return;
				}
			}
			// 2) ...else the BUYER name itself -> its first location, but only
			// after the user confirms in the modal.
			for (const buyer of lists?.buyerList || []) {
				if (!looseMatch(buyer?.label || buyer?.name, raw)) continue;
				const location = (buyer?.children || [])[0];
				if (location) {
					newHeaders.push({
						name: location.label || location.name,
						id: location.value,
						groupName: buyer?.label || buyer?.name,
					});
					buyerIdsToAdd.push(location.value);
					columnMap.push({ idx, headerId: location.value, isNew: true });
					buyerNameCols.push({
						raw,
						buyerName: buyer?.label || buyer?.name,
						locationName: location.label || location.name,
					});
					return;
				}
			}
		} else {
			const store = (lists?.storeList || []).find(
				(s) => [s?.name, s?.shortName, s?.label].some(
					(label) => label && looseMatch(label, raw)
				)
			);
			if (store) {
				newHeaders.push({
					name: `${store.name} ${store.shortName ? `(${store.shortName})` : ''}`,
					storeName: store.name,
					shortName: store.shortName,
					id: store.id,
				});
				columnMap.push({ idx, headerId: store.id, isNew: true });
				addedCols.push({ raw, resolvedName: store.name });
				return;
			}
		}

		// Not in the system at all -> whole column marked red. No qty is
		// mapped into it (it can't reach the order payload).
		newHeaders.push({
			name: raw,
			id: `__unknown_${norm(raw)}`,
			_unknownColumn: true,
		});
		unknownNames.push(raw);
	});

	return {
		columnMap,
		newHeaders,
		buyerIdsToAdd,
		unknownNames,
		buyerNameCols,
		addedCols,
	};
};

export const usePasteData = ({
	modeName,
	state,
	setState,
	lists,
	tableData,
	setTableData,
	productOptions,
	productList,
	setConfirmationModal,
	changeQtyRef,
}) => {
	const [isProcessingClipboard, setIsProcessingClipboard] = useState(false);

	const closePasteModal = () =>
		setConfirmationModal((prev) => ({ ...prev, ...CLOSED_CONFIRMATION }));

	const applyPasted = ({
		found,
		ambiguous,
		notFound,
		includeNotFound,
		columnMap,
		newHeaders,
		buyerIdsToAdd,
	}) => {
		// Pasted columns that matched un-selected buyer locations: select them
		// so the header build + downstream stay consistent.
		if (modeName === 'buyerOrder' && buyerIdsToAdd.length > 0) {
			const current = (state?.buyer || []).map((id) => Number(id));
			const merged = Array.from(
				new Set([...current, ...buyerIdsToAdd.map(Number)])
			);
			if (merged.length !== current.length) {
				setState((prev) => ({ ...prev, buyer: merged }));
			}
		}

		setTableData((prev) => {
			// Merge new columns in before Total/Actions.
			let headers = [...(prev?.headers || [])];
			if (newHeaders.length > 0) {
				const fresh = newHeaders.filter(
					(newHeader) => !headers.some((h) => `${h.id}` === `${newHeader.id}`)
				);
				const totalIndex = headers.findIndex((h) => h.id === 'Total');
				const insertAt = totalIndex >= 0 ? totalIndex : headers.length;
				headers = [
					...headers.slice(0, insertAt),
					...fresh,
					...headers.slice(insertAt),
				];
			}

			const dataColumns = headers.filter(
				(header) => !META_IDS.includes(header.id) && !header._unknownColumn
			);
			const firstColumnId = dataColumns[0]?.id;
			const hasColumnMap = columnMap.length > 0;

			const cellsFor = (unit, product = null) =>
				dataColumns.reduce((acc, column) => {
					acc[column.id] = {
						qty: '',
						unit,
						storeId: column.id,
						...(product
							? {
								itemId: product.variantId,
								productId: product.productId,
							}
							: {}),
					};
					return acc;
				}, {});

			// Column-wise qtys when the paste had store columns; otherwise the
			// single qty lands in the first data column.
			const fillQtys = (cells, pasted) => {
				const next = { ...cells };
				if (hasColumnMap) {
					columnMap.forEach(({ idx, headerId }) => {
						const qty = pasted[`col${idx}`];
						if (qty != null && !Number.isNaN(qty)) {
							next[headerId] = {
								...(next[headerId] || {}),
								qty: String(qty),
							};
						}
					});
				} else if (firstColumnId && pasted.qty != null) {
					next[firstColumnId] = {
						...(next[firstColumnId] || {}),
						qty: String(pasted.qty),
					};
				}
				return next;
			};

			const rows = (prev?.rows || []).map((row) => {
				// Existing rows get cell objects for any newly added columns.
				const patched = { ...row };
				dataColumns.forEach((column) => {
					if (!patched[column.id]) {
						patched[column.id] = {
							qty: '',
							unit: patched?.originalUnit
								? getUnitConvertedUnit(null, patched.originalUnit, true)
								: '',
							storeId: column.id,
							...(patched?.itemId ? { itemId: patched.itemId } : {}),
						};
					}
				});
				return patched;
			});

			found.forEach(({ pasted, product }) => {
				const existingIndex = rows.findIndex(
					(row) => row?.itemId === product.variantId
				);
				const unit = getUnitConvertedUnit(null, product.unit, true);
				if (existingIndex >= 0) {
					rows[existingIndex] = {
						...rows[existingIndex],
						...fillQtys(
							dataColumns.reduce((acc, column) => {
								acc[column.id] = rows[existingIndex][column.id] || {
									qty: '',
									unit,
									storeId: column.id,
									itemId: product.variantId,
									productId: product.productId,
								};
								return acc;
							}, {}),
							pasted
						),
					};
				} else {
					rows.push({
						'Item Name': product.viewOnlyLabel,
						itemId: product.variantId,
						SkuCode: product.clientVariantId,
						originalUnit: product.unit,
						Total: '',
						...fillQtys(cellsFor(unit, product), pasted),
					});
				}
			});

			ambiguous.forEach(({ pasted, candidates }) => {
				rows.push({
					'Item Name': pasted.name,
					itemId: null,
					SkuCode: pasted.sku || '',
					_isAmbiguous: true,
					_candidates: candidates,
					Total: '',
					...fillQtys(cellsFor(pasted.unit || ''), pasted),
				});
			});

			if (includeNotFound) {
				notFound.forEach(({ pasted }) => {
					rows.push({
						'Item Name': pasted.name,
						itemId: null,
						SkuCode: pasted.sku || '',
						_isNotFound: true,
						_excelName: pasted.name,
						Total: '',
						...fillQtys(cellsFor(pasted.unit || ''), pasted),
					});
				});
			}

			return {
				...prev,
				headers,
				rows: rows.map((row, index) => ({
					...row,
					'S.No': index + 1,
					Actions: { rowIndex: index },
				})),
			};
		});
	};

	const processClipboardData = async () => {
		changeQtyRef.current = false;
		setIsProcessingClipboard(true);
		try {
			const clipboardText = await navigator.clipboard.readText();
			if (!clipboardText) {
				notificationServices.generateNotification({
					type: 'warning',
					message: 'No data found in clipboard',
				});
				return;
			}

			// Store/location columns in the pasted header row = every header
			// that is not one of the meta columns (name/sku/unit/qty/...).
			const lines = String(clipboardText).split(/\r?\n/).filter((l) => l && l.trim());
			const headerCells = splitLine(lines[0] || '');
			const candidates = headerCells
				.map((raw, idx) => ({ raw, idx }))
				.filter(({ raw }) => raw &&
					!META_HEADER_KEYS.some((key) => norm(raw) === key));

			const resolved = resolvePastedColumns({
				candidates,
				headers: tableData?.headers || [],
				lists,
				modeName,
			});

			// Full = everything (new columns + buyer-name mappings).
			// Skipped = only already-existing columns + red unknown markers.
			const fullCols = {
				columnMap: resolved.columnMap,
				newHeaders: resolved.newHeaders,
				buyerIdsToAdd: resolved.buyerIdsToAdd,
			};
			const skippedCols = {
				columnMap: resolved.columnMap.filter((c) => !c.isNew),
				newHeaders: resolved.newHeaders.filter((h) => h._unknownColumn),
				buyerIdsToAdd: [],
			};

			// parsePastedTable does the row parsing; the store columns ride
			// along as extraColumns (keyed col<idx>).
			const parsed = parsePastedTable(
				clipboardText,
				[],
				candidates.map(({ raw, idx }) => ({
					key: `col${idx}`,
					aliases: [raw],
				}))
			);
			if (!parsed.length) {
				notificationServices.generateNotification({
					type: 'error',
					message: 'No valid rows found. Please copy Excel rows with a product name and quantity.',
				});
				return;
			}

			const found = [];
			const ambiguous = [];
			const notFound = [];
			parsed.forEach((pasted) => {
				const { row, candidates: productCandidates } =
					matchPastedRowToProduct({
						name: pasted.name,
						sku: pasted.sku,
						flattenedProducts: productOptions,
						productList,
						fuzzyMatcher: matchIngredientToProduct,
					});
				if (row) found.push({ pasted, product: row });
				else if (productCandidates?.length) {
					ambiguous.push({ pasted, candidates: productCandidates });
				} else notFound.push({ pasted });
			});

			const notifyResult = (includeNotFound) => {
				notificationServices.generateNotification({
					type: 'success',
					message: `Pasted ${found.length} matched product${found.length === 1 ? '' : 's'}${ambiguous.length ? `, ${ambiguous.length} need a pick from the dropdown` : ''}${includeNotFound && notFound.length ? `, ${notFound.length} not found (red rows)` : ''}${resolved.unknownNames.length ? `. Unknown columns marked red: ${resolved.unknownNames.join(', ')}` : ''}.`,
					config: { duration: 6000 },
				});
			};

			// Stage 2: products — not-found rows need a decision.
			const runProductStage = (cols) => {
				if (!notFound.length) {
					applyPasted({
						found,
						ambiguous,
						notFound,
						includeNotFound: false,
						...cols,
					});
					notifyResult(false);
					closePasteModal();
					return;
				}
				setConfirmationModal((prev) => ({
					...prev,
					isOpen: true,
					heading: 'Some Products Not Found',
					textAlign: true,
					width: 'w-[600px] max-w-2xl',
					message: (
						<div className='flex flex-col gap-2'>
							<p className='m-0 flex items-center gap-1.5 text-[13px] text-gray-700'>
								<Check className='h-4 w-4 text-green-600' />
								{`${found.length + ambiguous.length} product${found.length + ambiguous.length === 1 ? '' : 's'} matched`}
							</p>
							<p className='m-0 text-[13px] text-gray-600'>
								{`${notFound.length} product${notFound.length > 1 ? 's' : ''} could not be matched:`}
							</p>
							<div className='flex max-h-[200px] flex-col gap-1.5 overflow-y-auto'>
								{notFound.map(({ pasted }, index) => (
									<div
										key={index}
										className='flex items-center gap-2 rounded-md border border-red-100 bg-red-50 px-3 py-1.5'
									>
										<X className='h-3 w-3 flex-shrink-0 text-red-400' />
										<span className='truncate text-[12px] font-medium text-red-700'>
											{pasted.name}
										</span>
									</div>
								))}
							</div>
							<p className='m-0 mt-1 text-[12px] text-gray-500'>
								Go with all (not-found ones stay as red rows you can
								fix), or go with the found ones only?
							</p>
						</div>
					),
					customButton: [
						{
							label: 'Go with Found Only',
							onClick: () => {
								applyPasted({
									found,
									ambiguous,
									notFound,
									includeNotFound: false,
									...cols,
								});
								notifyResult(false);
								closePasteModal();
							},
							className: 'bg-blue-600 hover:bg-blue-700 text-white',
						},
						{
							label: 'Go with All',
							onClick: () => {
								applyPasted({
									found,
									ambiguous,
									notFound,
									includeNotFound: true,
									...cols,
								});
								notifyResult(true);
								closePasteModal();
							},
							className: 'bg-gray-100 hover:bg-gray-200 text-gray-700',
						},
						{
							label: 'Cancel',
							onClick: closePasteModal,
							className: 'text-gray-400 hover:text-gray-600',
						},
					],
				}));
			};

			// Stage 1: column assumptions — buyer-NAME columns (qty goes to the
			// buyer's first location), new columns being added, and unknown
			// (red) columns are all surfaced to the user first.
			const hasAssumptions =
				resolved.buyerNameCols.length > 0 ||
				resolved.addedCols.length > 0 ||
				resolved.unknownNames.length > 0;
			if (!hasAssumptions) {
				runProductStage(fullCols);
				return;
			}

			setConfirmationModal((prev) => ({
				...prev,
				isOpen: true,
				heading: 'Confirm Pasted Columns',
				textAlign: true,
				width: 'w-[600px] max-w-2xl',
				message: (
					<div className='flex flex-col gap-3'>
						{resolved.buyerNameCols.length > 0 && (
							<div className='flex flex-col gap-1.5'>
								<p className='m-0 flex items-center gap-1.5 text-[13px] font-medium text-gray-700'>
									<Building2 className='h-4 w-4 text-amber-600' />
									These pasted columns are BUYER names — the qty
									will be pasted at the buyer's first location:
								</p>
								{resolved.buyerNameCols.map((entry, index) => (
									<div
										key={index}
										className='flex items-center gap-2 rounded-md border border-amber-100 bg-amber-50 px-3 py-1.5'
									>
										<span className='truncate text-[12px] font-medium text-gray-800'>
											{entry.raw}
										</span>
										<span className='text-[12px] text-gray-400'>→</span>
										<span className='truncate text-[12px] text-amber-700'>
											{`${entry.buyerName} — ${entry.locationName}`}
										</span>
									</div>
								))}
							</div>
						)}
						{resolved.addedCols.length > 0 && (
							<div className='flex flex-col gap-1.5'>
								<p className='m-0 flex items-center gap-1.5 text-[13px] font-medium text-gray-700'>
									<Columns3 className='h-4 w-4 text-blue-600' />
									These columns are not in your table yet and
									will be ADDED:
								</p>
								{resolved.addedCols.map((entry, index) => (
									<div
										key={index}
										className='flex items-center gap-2 rounded-md border border-blue-100 bg-blue-50 px-3 py-1.5'
									>
										<span className='truncate text-[12px] font-medium text-gray-800'>
											{entry.raw}
										</span>
										<span className='text-[12px] text-gray-400'>→</span>
										<span className='truncate text-[12px] text-blue-700'>
											{entry.resolvedName}
										</span>
									</div>
								))}
							</div>
						)}
						{resolved.unknownNames.length > 0 && (
							<div className='flex flex-col gap-1.5'>
								<p className='m-0 flex items-center gap-1.5 text-[13px] font-medium text-gray-700'>
									<X className='h-4 w-4 text-red-500' />
									These columns are NOT in the system — they
									will show as RED columns (no qty goes into
									them). Use the ✕ on the column header to
									remove them:
								</p>
								{resolved.unknownNames.map((name, index) => (
									<div
										key={index}
										className='flex items-center gap-2 rounded-md border border-red-100 bg-red-50 px-3 py-1.5'
									>
										<span className='truncate text-[12px] font-medium text-red-700'>
											{name}
										</span>
										<span className='ml-auto text-[11px] text-red-400'>
											Not in system
										</span>
									</div>
								))}
							</div>
						)}
						<p className='m-0 text-[12px] text-gray-500'>
							Want to go with this? Or skip these columns and paste
							only into the existing ones?
						</p>
					</div>
				),
				customButton: [
					{
						label: 'Go with This',
						onClick: () => {
							closePasteModal();
							runProductStage(fullCols);
						},
						className: 'bg-blue-600 hover:bg-blue-700 text-white',
					},
					{
						label: 'Skip These Columns',
						onClick: () => {
							closePasteModal();
							runProductStage(skippedCols);
						},
						className: 'bg-gray-100 hover:bg-gray-200 text-gray-700',
					},
					{
						label: 'Cancel',
						onClick: closePasteModal,
						className: 'text-gray-400 hover:text-gray-600',
					},
				],
			}));
		} catch (error) {
			console.error('Error processing clipboard:', error);
			notificationServices.generateNotification({
				type: 'error',
				message: 'Error reading clipboard. Please ensure you have copied the data correctly.',
			});
		} finally {
			setIsProcessingClipboard(false);
		}
	};

	return { isProcessingClipboard, processClipboardData };
};

export default usePasteData;
